import {
  SHAPES,
  type ScatterFrame,
  type ScatterItem,
  type ScatterRound,
  type SequentialItem,
  type SequentialRound,
  type Shape,
} from '@clicklinx/protocol'
import { Rng } from './rng.js'
import { placeAwayFromPrevious, planGrid, scatterOnGrid } from './layout.js'
import {
  SCATTER,
  SEQUENTIAL,
  scatterDistractorVariety,
  scatterFrameMs,
  scatterItemsPerFrame,
  sequentialConcurrency,
  sequentialDesiredCount,
  sequentialPaceMs,
} from './ramp.js'

/**
 * Mode 2. A single shape flashes in and out; the answer is how many times it
 * appeared.
 *
 * The round's length is derived from the pace and the item count rather than
 * being padded out to the host's window, which is what makes it feel brisk.
 * The host's window is honoured as a ceiling.
 */
export function generateSequentialRound(
  seed: number,
  roundIndex: number,
  baseSeconds: number,
): SequentialRound {
  const rng = new Rng(seed)
  const ceilingMs = baseSeconds * 1000
  const paceMs = sequentialPaceMs(roundIndex)
  const concurrency = sequentialConcurrency(roundIndex)

  // Never plan more items than the host's window can hold at this pace.
  const maxByCeiling = Math.max(3, concurrency * Math.floor(ceilingMs / paceMs))
  const count = clamp(
    sequentialDesiredCount(roundIndex),
    3,
    Math.min(maxByCeiling, SEQUENTIAL.maxItems),
  )

  const slots = Math.ceil(count / concurrency)
  const displayMs = Math.min(ceilingMs, slots * paceMs)
  const visibleMs = clamp(
    Math.round(paceMs * SEQUENTIAL.visibleFraction),
    SEQUENTIAL.minVisibleMs,
    SEQUENTIAL.maxVisibleMs,
  )

  const targetShape = rng.pick(SHAPES)
  const items: SequentialItem[] = []
  let previous: { x: number; y: number } | null = null

  for (let i = 0; i < count; i++) {
    const slotIndex = Math.floor(i / concurrency)
    const size = rng.float(0.075, 0.115)
    const placement = placeAwayFromPrevious(rng, previous, size)
    previous = { x: placement.x, y: placement.y }

    const appearAt = Math.min(
      Math.round(slotIndex * paceMs + rng.float(0, paceMs * 0.2)),
      Math.max(0, displayMs - 60),
    )
    const disappearAt = Math.min(appearAt + visibleMs, displayMs)

    items.push({
      id: `s${roundIndex}-${i}`,
      shape: targetShape,
      x: placement.x,
      y: placement.y,
      size: placement.size,
      rotation: placement.rotation,
      appearAt,
      disappearAt: Math.max(disappearAt, appearAt + 1),
    })
  }

  items.sort((a, b) => a.appearAt - b.appearAt)

  return { kind: 'count_sequential', roundIndex, displayMs, targetShape, items }
}

/**
 * Mode 3. The player is told which shape to count, then the field is replaced
 * wholesale every few seconds. The answer is the running total of that shape
 * across every screenful, not the count on any one of them.
 *
 * That is what makes this different from a search puzzle: you cannot re-check
 * a screen you have already lost, so the count has to be carried forward.
 */
export function generateScatterRound(
  seed: number,
  roundIndex: number,
  baseSeconds: number,
): ScatterRound {
  const rng = new Rng(seed)
  const ceilingMs = baseSeconds * 1000
  const frameMs = scatterFrameMs(roundIndex)

  const frameCount = clamp(
    Math.floor(ceilingMs / frameMs),
    SCATTER.minFrames,
    SCATTER.maxFrames,
  )
  const displayMs = frameCount * frameMs

  const targetShape = rng.pick(SHAPES)
  const distractorPool = rng.sample(
    SHAPES.filter((s) => s !== targetShape),
    scatterDistractorVariety(roundIndex),
  )
  const perFrame = scatterItemsPerFrame(roundIndex)

  const frames: ScatterFrame[] = []
  for (let f = 0; f < frameCount; f++) {
    // Between a sixth and a third of the field, so the target is neither the
    // obvious majority nor a needle in a haystack.
    const targetCount = clamp(
      Math.round(perFrame * rng.float(0.15, 0.32)),
      1,
      Math.max(1, perFrame - 2),
    )

    const shapes: Shape[] = [
      ...Array.from({ length: targetCount }, () => targetShape),
      ...Array.from({ length: perFrame - targetCount }, () => rng.pick(distractorPool)),
    ]
    const order = rng.shuffle(shapes)
    const placements = scatterOnGrid(rng, perFrame)

    frames.push({
      index: f,
      startAt: f * frameMs,
      endAt: (f + 1) * frameMs,
      items: placements.map((placement, i) => ({
        id: `c${roundIndex}-${f}-${i}`,
        shape: order[i] as Shape,
        x: placement.x,
        y: placement.y,
        size: placement.size,
        rotation: placement.rotation,
      })) satisfies ScatterItem[],
    })
  }

  return { kind: 'count_scatter', roundIndex, displayMs, targetShape, frameMs, frames }
}

/**
 * The single source of truth for a round's correct answer. Grading never
 * re-derives the number by another route - it counts the payload that was
 * actually sent.
 */
export function correctAnswerFor(round: SequentialRound | ScatterRound): number {
  if (round.kind === 'count_sequential') return round.items.length
  return round.frames.reduce(
    (total, frame) => total + frame.items.filter((item) => item.shape === round.targetShape).length,
    0,
  )
}

/** Which Scatter frame is on screen at a given point in the round. */
export function scatterFrameAt(round: ScatterRound, elapsedMs: number): ScatterFrame | null {
  if (elapsedMs < 0) return null
  return (
    round.frames.find((frame) => elapsedMs >= frame.startAt && elapsedMs < frame.endAt) ?? null
  )
}

/** Exposed for the client so it can size its own preview of a round. */
export { planGrid }

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
